import type { ChatSection, Conversation } from '@tradeoutlet/shared';

export interface SectionNode {
  section: ChatSection;
  depth: number;
  children: SectionNode[];
  channels: Conversation[];
  /** Total channels in this subtree (for collapsed badges). */
  total: number;
  unread: number;
}

const byPos = <T extends { position: number; name: string }>(a: T, b: T) => a.position - b.position || a.name.localeCompare(b.name);
const convPos = (a: Conversation, b: Conversation) => (a.section_position ?? 0) - (b.section_position ?? 0) || (a.slug ?? '').localeCompare(b.slug ?? '');

/**
 * Builds the sidebar tree from the sections and the channels the user can already see.
 * Sections never grant access: a channel the user can't see is simply not in `channels`.
 * Empty branches are pruned unless `keepEmpty` (managers organising the tree).
 * Channels pointing at an unknown section fall back to the top level.
 */
export function buildSectionTree(sections: ChatSection[], channels: Conversation[], keepEmpty = false): { roots: SectionNode[]; loose: Conversation[] } {
  const known = new Set(sections.map(s => s.id));
  const childrenOf = new Map<string | null, ChatSection[]>();
  for (const s of sections) {
    const parent = s.parent_id && known.has(s.parent_id) ? s.parent_id : null;
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), s]);
  }
  const channelsOf = new Map<string, Conversation[]>();
  const loose: Conversation[] = [];
  for (const c of channels) {
    if (c.section_id && known.has(c.section_id)) channelsOf.set(c.section_id, [...(channelsOf.get(c.section_id) ?? []), c]);
    else loose.push(c);
  }
  const visit = (s: ChatSection, depth: number, seen: Set<string>): SectionNode | null => {
    if (seen.has(s.id)) return null; // defensive: never loop on bad data
    seen.add(s.id);
    const children = (childrenOf.get(s.id) ?? []).sort(byPos).map(c => visit(c, depth + 1, seen)).filter((n): n is SectionNode => Boolean(n));
    const own = (channelsOf.get(s.id) ?? []).sort(convPos);
    const total = own.length + children.reduce((n, c) => n + c.total, 0);
    const unread = own.filter(c => c.unread_count > 0 && c.notify_level !== 'none').length + children.reduce((n, c) => n + c.unread, 0);
    if (!keepEmpty && total === 0) return null;
    return { section: s, depth, children, channels: own, total, unread };
  };
  const seen = new Set<string>();
  const roots = (childrenOf.get(null) ?? []).sort(byPos).map(s => visit(s, 0, seen)).filter((n): n is SectionNode => Boolean(n));
  // t446u: channels without a section follow their saved order too (then name), so they can be re-ordered.
  return { roots, loose: loose.sort(convPos) };
}

/** "Retail Stores › Merchandising" for a section id (one rule, in @tradeoutlet/shared). */
export { sectionPath } from '@tradeoutlet/shared';
